import axios from 'axios';
import { configRefs } from '@utils/config';
import { courseToSimilarityKey } from '../utils/course-similarity.js';
import { mapApiResponseToCourseModel } from './utils/api-to-course.js';
import { mapCourseModelToApiRequest } from './utils/course-to-api.js';

// Tracks whether or not this client is already submitting an update request to
// the backend.
// This is required to ensure that the de-synced prompt cannot be triggered by
// the same client by trigger many update requests in parallel.
//
// This method of checking is only valid since we can safely assume that two
// clients cannot communicate with the backend at the same time (since the
// actual user can only interact with one client at a time)
let updateBlocksRequestCount = 0;
async function updateCourseRequest(notifier, course, user,
// NB: The similarityKey should be a key based on the current course BEFORE
// modifications have been applied.
options) {
  if (!user) {
    throw new Error("A user must be provided when updating courses");
  }

  // Only synchronize with the backend if the client isn't currently submitting
  // another update. This ensures that the current client cannot accidentally
  // trigger the de-synced message by itself.
  if (!updateBlocksRequestCount && options.includeBlocks) {
    const response = await axios.get(`${await configRefs.apis.userdata.get()}/api/v2/Courses/${encodeURIComponent(course.id)}`);
    const remoteCourse = mapApiResponseToCourseModel(user, response.data);

    // Check if the current course has been changed elsewhere
    if (!updateBlocksRequestCount && options.similarityKey !== courseToSimilarityKey(remoteCourse)) {
      const syncWithApi = await notifier.confirm({
        heading: "Du arbejder med det samme forløb i flere vinduer",
        body: "Hvis du tilføjer indhold til dette forløb, vil dine ændringer i det andet vindue gå tabt. Behold og arbejd videre i denne version eller opdatér forløbet og arbejd i den seneste version.",
        requiresAction: true,
        labels: {
          ok: "Opdatér forløb",
          cancel: "Behold denne version"
        }
      });

      // If we should not force our update, then we return the remote course to
      // maintain consistency afterwards
      if (syncWithApi === "ok") {
        return {
          ...remoteCourse,
          __internalId: course.__internalId
        };
      }
    }
  }
  try {
    if (options.includeBlocks) {
      updateBlocksRequestCount += 1;
    }
    await axios.put(`${await configRefs.apis.userdata.get()}/api/v2/Courses?id=${encodeURIComponent(course.id)}`, mapCourseModelToApiRequest(user, course, options));
  } finally {
    if (options.includeBlocks) {
      updateBlocksRequestCount -= 1;
    }
  }
  return {
    ...course,
    modifiedAt: new Date()
  };
}

export { updateCourseRequest };
//# sourceMappingURL=request.update-course.js.map
